import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  styled,
  Box,
  Divider,
} from '@mui/material';
import {
  OnboardingStepStatus,
  OnboardingStep,
} from 'further-types/lp/onboarding';
import JourneyStepStatus from './JourneyStepStatus';
import { ExpandMore } from '@mui/icons-material';
import { Column } from 'further-ui/components/Layout';
import { VehicleApplication, InvestorTaxDetails } from '../../types';
import { CalendarTime } from 'further-ui/utils';
import ReviewActions from './ReviewActions';
import JourneyStepChangelogEntry from './JourneyStepChangelogEntry';
import ReviewedStepDetails from './ReviewedStepDetails';
import PreviousResults from './ReviewedStepDetails/PreviousResults';
import { useDisclosure } from 'further-ui/hooks';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  borderTop: `1px solid ${theme.palette.grey[300]}`,
  width: '100%',
}));

const Accordion = styled(MuiAccordion)({
  width: '100%',
});

const AccordionSummary = styled(MuiAccordionSummary)({
  margin: 0,
  padding: 0,
  minHeight: 0,
  '& .MuiAccordionSummary-content': {
    margin: 0,
    padding: 0,
  },
});

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(theme.spacingSizes.sm, 0, 0, 0),
}));

type Props = {
  step: {
    key: OnboardingStep;
    status?: OnboardingStepStatus | null;
    canUnapprove: boolean;
    changeLog?: Array<{
      status: OnboardingStepStatus;
      changedBy?: {
        firstName?: string;
        lastName?: string;
        profilePic?: string;
      };
      changedAt: CalendarTime;
    }>;
    showStatus: boolean;
  };
  application: VehicleApplication;
  taxDetails: InvestorTaxDetails | null;
  firmDomain: string;
  showDetails: boolean;
};

const JourneyStepReview: React.FC<Props> = ({
  step,
  application,
  taxDetails,
  firmDomain,
  showDetails,
}) => {
  const { status, changeLog, showStatus } = step;
  const manualKycDisclosure = useDisclosure();

  if (!showStatus || !status || status === OnboardingStepStatus.Disabled)
    return null;

  if (
    !showDetails ||
    (status === OnboardingStepStatus.Requested &&
      step.key === OnboardingStep.InvestorDetails)
  ) {
    return (
      <Container>
        <JourneyStepStatus status={status} />
      </Container>
    );
  }

  return (
    <Container>
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMore fontSize="small" />}>
          <JourneyStepStatus status={status} />
        </AccordionSummary>
        <AccordionDetails>
          <Column fullWidth spacing="lg">
            <Divider />
            <PreviousResults
              vehicleId={application.vehicleId}
              vehicleApplicationId={application.id}
              step={step.key}
            />
            {changeLog?.length ? (
              changeLog.map((entry, index) => (
                <JourneyStepChangelogEntry
                  key={entry.changedAt.toUTCTime().toISOString()}
                  entry={entry}
                  application={application}
                  firmDomain={firmDomain}
                  isFirstEntry={index === 0}
                  step={step.key}
                >
                  <ReviewedStepDetails
                    investorId={application.investorId}
                    step={step.key}
                    taxDetails={taxDetails}
                    vehicleId={application.vehicleId}
                    vehicleApplicationId={application.id}
                    manualKycDisclosure={manualKycDisclosure}
                  />
                </JourneyStepChangelogEntry>
              ))
            ) : (
              <ReviewedStepDetails
                investorId={application.investorId}
                step={step.key}
                taxDetails={taxDetails}
                vehicleId={application.vehicleId}
                vehicleApplicationId={application.id}
                manualKycDisclosure={manualKycDisclosure}
              />
            )}

            <ReviewActions
              vehicleId={application.vehicleId}
              vehicleApplicationId={application.id}
              step={step}
              manualKycDisclosure={manualKycDisclosure}
            />
          </Column>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default JourneyStepReview;
